import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft, ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { BLOG_POSTS } from '../data/blogData';
export function BlogPost() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const post = BLOG_POSTS.find(p => p.id === Number(id));
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!post) {
    return <div className="min-h-screen bg-brand-light flex flex-col items-center justify-center text-center px-4 py-32" data-id="element-486" data-mp="3agv08tz1l2xz">
        <h1 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-487" data-mp="36gsw8e5u3xfj">
          Article not found
        </h1>
        <p className="text-slate-600 mb-8" data-id="element-488" data-mp="31i98q9bledl2">
          The article you are looking for doesn't exist or has been moved.
        </p>
        <Button href="/blog" variant="primary" icon={<ArrowLeft className="w-5 h-5" data-id="element-490" data-mp="zput5gwitl7t" />} data-id="element-489" data-mp="14m22ljo4leui">
          Back to Blog
        </Button>
      </div>;
  }
  const related = BLOG_POSTS.filter(p => p.id !== post.id).slice(0, 3);
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-491" data-mp="165q5t952p2yj">
      {/* Hero */}
      <div className="relative bg-brand-navy" data-id="element-492" data-mp="s5ffq8bl7sef">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-493" data-mp="s6qiq5x7kw4h" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/80 to-brand-navy/50" data-id="element-494" data-mp="1kst5nmgvbnp1" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24" data-id="element-495" data-mp="1otd0gjdyyf80">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-200 hover:text-white text-sm font-medium mb-6 transition-colors" data-id="element-496" data-mp="1b26x2c7kjxfu">
            <ArrowLeft className="w-4 h-4" data-id="element-497" data-mp="23rdvlb70wn13" /> Back to Blog
          </Link>
          <span className="inline-block bg-brand-red text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full mb-4" data-id="element-498" data-mp="28inhc0pgb67p">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-6" data-id="element-499" data-mp="2icjibwetv7ag">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-200 text-sm" data-id="element-500" data-mp="28fevrvh2l9t4">
            <span className="flex items-center gap-2" data-id="element-501" data-mp="23i51a1etq7yf">
              <Calendar className="w-4 h-4 text-brand-gold" data-id="element-502" data-mp="23jpr5dq712py" /> {post.date}
            </span>
            <span className="flex items-center gap-2" data-id="element-503" data-mp="1zhuq8ac6crcp">
              <Clock className="w-4 h-4 text-brand-gold" data-id="element-504" data-mp="1uo0bllbff2kh" /> {post.readTime}
            </span>
            <span className="flex items-center gap-2" data-id="element-505" data-mp="1pp5h3m43jpkj">
              <User className="w-4 h-4 text-brand-gold" data-id="element-506" data-mp="1pnm0fuej3ci8" /> {post.author}
            </span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-507" data-mp="1fqpu9vc3r2gu">
        <div className="bg-white rounded-3xl shadow-soft border border-slate-100 p-8 md:p-12" data-id="element-508" data-mp="1bpnrj4iz88a4">
          {post.content.map((block, idx) => {
          if (block.type === 'heading') {
            return <h2 key={idx} className="text-2xl md:text-3xl font-heading font-bold text-brand-navy mt-10 mb-4 first:mt-0" data-id="element-509" data-mp="3re31qrdvhycr">
                  {block.text}
                </h2>;
          }
          if (block.type === 'subheading') {
            return <h3 key={idx} className="text-lg md:text-xl font-heading font-bold text-brand-red mt-6 mb-3" data-id="element-510" data-mp="33rd1nvl34dus">
                  {block.text}
                </h3>;
          }
          if (block.type === 'list') {
            return <ul key={idx} className="space-y-2 mb-6" data-id="element-511" data-mp="3igiyjcwg9rzi">
                  {block.items.map(item => <li key={item} className="flex items-start gap-3" data-id="element-512" data-mp="2kx7tfvrn6wdp">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" data-id="element-513" data-mp="2pwkdy9v1jrd4" />
                      <span className="text-slate-600 leading-relaxed" data-id="element-514" data-mp="vigaucthz5jm">
                        {item}
                      </span>
                    </li>)}
                </ul>;
          }
          return <p key={idx} className="text-slate-600 text-lg leading-relaxed mb-5" data-id="element-515" data-mp="10gzzwm3e0xg7" data-mpi={idx}>
                {block.text}
              </p>;
        })}

          {/* In-article CTA */}
          <div className="mt-10 rounded-2xl bg-brand-navy p-8 text-center" data-id="element-516" data-mp="1a5rn6gxi6tks">
            <h3 className="text-xl font-heading font-bold text-white mb-2" data-id="element-517" data-mp="1o742qr8r4t65">
              Your Health Is Our Devotion
            </h3>
            <p className="text-slate-200 text-sm mb-6" data-id="element-518" data-mp="1j8sp05fc5yin">
              Book an appointment or chat with us to discuss your health
              concerns.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-519" data-mp="3kze1ttmx0qt6">
              <Button href="/book" variant="primary" data-id="element-520" data-mp="20gtmtwtp1zdv">
                Book an Appointment
              </Button>
              <a href="https://wa.me/26773241613" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-medium px-6 py-3 hover:bg-[#20bd5a] transition-colors" data-id="element-521" data-mp="25gg1map67nos">
                <MessageCircle className="w-5 h-5" data-id="element-522" data-mp="1hpaqg8u6djkt" />
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-20" data-id="element-523" data-mp="18qfj2tfea3fx">
        <h2 className="text-2xl font-heading font-bold text-brand-navy mb-8" data-id="element-524" data-mp="1cqzcpn0reoxb">
          More Health Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-525" data-mp="333f3czupzltj">
          {related.map(p => <Link key={p.id} to={`/blog/${p.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-soft-lg transition-all duration-300 border border-slate-100 group flex flex-col" data-id="element-526" data-mp="375junrxp46d0">
              <div className="h-44 overflow-hidden" data-id="element-527" data-mp="f02pht6x88kx">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-528" data-mp="awxoxw6eigyx" />
              </div>
              <div className="p-6 flex flex-col flex-grow" data-id="element-529" data-mp="12peu0frazsgw">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-red mb-2" data-id="element-530" data-mp="yms3qjaa6flt">
                  {p.category}
                </span>
                <h3 className="text-lg font-heading font-bold text-brand-navy mb-3 group-hover:text-brand-red transition-colors line-clamp-2" data-id="element-531" data-mp="ovxdz4xtsxvm">
                  {p.title}
                </h3>
                <span className="inline-flex items-center gap-2 text-brand-navy font-semibold text-sm mt-auto group-hover:text-brand-red transition-colors" data-id="element-532" data-mp="1md4j2f7uvemf">
                  Read More <ArrowRight className="w-4 h-4" data-id="element-533" data-mp="1oxalujkqgz1e" />
                </span>
              </div>
            </Link>)}
        </div>
      </section>
    </div>;
}